import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Clock, ExternalLink, ArrowRight, Send } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const T = {
  heroLabel: { de: 'Kontakt', en: 'Contact' },
  heroTitle1: { de: 'Sprechen Sie', en: 'Talk' },
  heroTitle2: { de: 'mit uns', en: 'to Us' },
  heroSub: { de: 'Wir freuen uns auf Ihre Anfrage und erstellen Ihnen gerne ein individuelles Angebot.', en: 'We look forward to your enquiry and are happy to provide you with a customised quote.' },
  address: { de: 'Adresse', en: 'Address' },
  email: { de: 'E-Mail', en: 'Email' },
  phone: { de: 'Telefon', en: 'Phone' },
  website: { de: 'Website', en: 'Website' },
  availability: { de: 'Erreichbarkeit', en: 'Availability' },
  availabilityVal: { de: 'Mo–Fr: 07:00 – 18:00 Uhr | Sa: nach Vereinbarung', en: 'Mon–Fri: 07:00 – 18:00 | Sat: by appointment' },
  mgmt: { de: 'Geschäftsführung', en: 'Management' },
  sendMsg: { de: 'Nachricht senden', en: 'Send Message' },
  name: { de: 'Name *', en: 'Name *' },
  namePh: { de: 'Ihr Name', en: 'Your name' },
  emailLabel: { de: 'E-Mail *', en: 'Email *' },
  phonePh: { de: 'Telefon', en: 'Phone' },
  msgLabel: { de: 'Ihre Nachricht *', en: 'Your Message *' },
  msgPh: { de: 'Beschreiben Sie Ihren Transportbedarf...', en: 'Describe your transport needs...' },
  submitBtn: { de: 'Nachricht senden', en: 'Send Message' },
  rentVehicle: { de: 'Fahrzeug mieten', en: 'Rent Vehicle' },
  onlineBooking: { de: 'Online-Buchung →', en: 'Online Booking →' },
  callDirect: { de: 'Direkt anrufen', en: 'Call Directly' },
  thankYou: { de: 'Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.', en: 'Thank you for your message! We will get back to you as soon as possible.' },
};

export default function AtoBLogisticKontakt() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const { language } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(T.thankYou[language]);
  };

  return (
    <main data-testid="sub-atob-logistic-kontakt" className="bg-[#030f1c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5A623]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-[2px] bg-[#F5A623]" />
              <span className="text-[#F5A623] text-sm font-semibold tracking-[0.2em] uppercase">{T.heroLabel[language]}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {T.heroTitle1[language]}<br />
              <span className="text-[#F5A623]">{T.heroTitle2[language]}</span>
            </h1>
            <p className="text-xl text-white/60">
              {T.heroSub[language]}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#081a30] rounded-2xl border border-[#1a365d] p-8 md:p-10 mb-8">
                <h2 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  AtoB Logistic Solutions GmbH
                </h2>
                
                <div className="space-y-6">
                  <ContactItem 
                    icon={MapPin} 
                    label={T.address[language]} 
                    value="Hanauer Landstraße 291A, 60314 Frankfurt am Main" 
                  />
                  <ContactItem 
                    icon={Mail} 
                    label={T.email[language]} 
                    value="info@atob-logistics.de" 
                    link="mailto:info@atob-logistics.de" 
                  />
                  <ContactItem 
                    icon={Phone} 
                    label={T.phone[language]} 
                    value="02173 2046451" 
                    link="tel:+4921732046451" 
                  />
                  <ContactItem 
                    icon={ExternalLink} 
                    label={T.website[language]} 
                    value="www.atob-logistics.de" 
                    link="https://www.atob-logistics.de" 
                  />
                  <ContactItem 
                    icon={Clock} 
                    label={T.availability[language]} 
                    value={T.availabilityVal[language]} 
                  />
                </div>
              </div>

              {/* Management */}
              <div className="bg-gradient-to-r from-[#F5A623]/10 to-transparent rounded-xl p-6 border border-[#F5A623]/20">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">{T.mgmt[language]}</p>
                <p className="text-white font-semibold text-lg">Admir Basic</p>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-xl overflow-hidden border border-[#1a365d] h-[250px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2558.5!2d8.71!3d50.11!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDA2JzM2LjAiTiA4wrA0MicyMi44IkU!5e0!3m2!1sde!2sde!4v1!5m2!1sde!2sde"
                  className="w-full h-full border-0 grayscale opacity-80"
                  allowFullScreen=""
                  loading="lazy"
                  title="Standort AtoB Logistic"
                ></iframe>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-[#081a30] rounded-2xl border border-[#1a365d] p-8 md:p-10">
                <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {T.sendMsg[language]}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{T.name[language]}</label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-[#030f1c] border border-[#1a365d] rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#F5A623] focus:outline-none transition-colors" placeholder={T.namePh[language]} />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">{T.emailLabel[language]}</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-[#030f1c] border border-[#1a365d] rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#F5A623] focus:outline-none transition-colors" placeholder="ihre@email.de" />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">{T.phonePh[language]}</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-[#030f1c] border border-[#1a365d] rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#F5A623] focus:outline-none transition-colors" placeholder="+49 ..." />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{T.msgLabel[language]}</label>
                    <textarea required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full bg-[#030f1c] border border-[#1a365d] rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-[#F5A623] focus:outline-none transition-colors resize-none" placeholder={T.msgPh[language]}></textarea>
                  </div>
                  <button type="submit" className="w-full bg-[#F5A623] text-[#030f1c] py-4 font-bold tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-white transition-colors rounded-lg">
                    {T.submitBtn[language]} <Send size={18} />
                  </button>
                </form>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <Link
                  to="/atob-logistic/vermietung"
                  className="bg-[#081a30] border border-[#1a365d] hover:border-[#F5A623]/50 rounded-xl p-6 text-center transition-all"
                >
                  <span className="text-[#F5A623] text-sm font-semibold block mb-1">{T.rentVehicle[language]}</span>
                  <span className="text-white/50 text-xs">{T.onlineBooking[language]}</span>
                </Link>
                <a
                  href="tel:+4921732046451"
                  className="bg-[#081a30] border border-[#1a365d] hover:border-[#F5A623]/50 rounded-xl p-6 text-center transition-all"
                >
                  <span className="text-[#F5A623] text-sm font-semibold block mb-1">{T.callDirect[language]}</span>
                  <span className="text-white/50 text-xs">02173 2046451</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ContactItem({ icon: Icon, label, value, link }) {
  const Content = () => (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-[#F5A623]/10 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon size={20} className="text-[#F5A623]" />
      </div>
      <div>
        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className="text-white">{value}</p>
      </div>
    </div>
  );

  if (link) {
    return (
      <a href={link} target={link.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">
        <Content />
      </a>
    );
  }
  return <Content />;
}
